<template>
  <section
    class="article-in-this-series-module-component"
    :class="{ 'no-media-before': noMediaBefore }">
    <div class="sa-container">
      <div
        class="module-layout"
        :class="{ 'is-series-collapsed': isCollapsed }">
        <div
          v-if="data.left_content?.content"
          class="left-content"
          v-html="data.left_content.content"></div>
      </div>
    </div>

    <aside
      v-if="articles.length"
      class="series-panel"
      :class="{ 'is-collapsed': isCollapsed }"
      @transitionend="handlePanelTransitionEnd">
      <button
        v-show="isCollapsed"
        class="collapsed-trigger"
        type="button"
        aria-label="Expand In this series"
        @click="expandPanel">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true">
          <path
            d="M20 12H4M10 18L4 12L10 6"
            stroke="#70FFE5"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
        <span>In this series</span>
      </button>

      <div
        class="series-content"
        :class="{
          'is-content-visible': contentVisible && !isCollapsed,
        }">
        <header class="series-header">
          <h2>In this series</h2>
          <button
            class="collapse-trigger"
            type="button"
            aria-label="Collapse In this series"
            @click="collapsePanel">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true">
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round" />
            </svg>
          </button>
        </header>

        <div class="series-list-frame">
          <div
            ref="seriesListRef"
            class="series-list"
            :style="{ height: seriesListHeight }"
            @scroll="updateArrowDirection">
            <div
              v-for="article in articles"
              :key="article?.ID || article?.id || article?.permalink"
              class="series-card">
              <BicolorLine
                :full="true"
                left="approachable"
                right="progressive" />

              <div class="card-content">
                <a
                  class="card-image"
                  :href="articleUrl(article)"
                  :aria-label="plainText(article?.post_title)">
                  <img
                    v-if="articleImage(article).url"
                    :src="articleImage(article).url"
                    :alt="articleImage(article).alt || ''" />

                  <span
                    class="card-player"
                    aria-hidden="true">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="34"
                      height="39"
                      viewBox="0 0 34 39"
                      fill="none">
                      <path
                        d="M32.2165 16.4084C34.2165 17.5631 34.2166 20.4499 32.2166 21.6046L4.49999 37.6068C2.49999 38.7615 -1.33145e-05 37.3181 -1.32135e-05 35.0087L-1.18146e-05 3.00435C-1.17136e-05 0.694952 2.49999 -0.748427 4.49999 0.406273L32.2165 16.4084Z"
                        fill="white" />
                    </svg>
                  </span>
                </a>

                <div class="card-text">
                  <h3
                    v-if="article?.post_title"
                    v-html="article.post_title"></h3>

                  <div
                    v-if="article?.acf?.description"
                    class="description"
                    v-html="article.acf.description"></div>
                </div>
              </div>
            </div>
          </div>

          <button
            v-if="articles.length > 4"
            class="scroll-control"
            type="button"
            :aria-label="
              isAtBottom
                ? 'Return to the first article in this series'
                : 'Show more articles in this series'
            "
            @click="scrollSeries">
            <svg
              :class="{ 'rotate-180': isAtBottom }"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true">
              <path
                d="M10 3V17M4.5 11.5L10 17L15.5 11.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  </section>
</template>

<script setup>
import useFooterBackgroundClass from '@/components/composables/useFooterBackgroundClass.js'
import useUtils from '@/components/composables/useUtils.js'
import BicolorLine from '@/components/utility/BicolorLine.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const humbleFooterClass = 'article-in-this-series-humble-footer'

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  noMediaBefore: {
    type: Boolean,
    default: false,
  },
})

useFooterBackgroundClass(
  humbleFooterClass,
  () => props.data?.update_footer_background
)

const { device } = useUtils()
const collapsed = ref(false)
const contentVisible = ref(true)
const seriesListRef = ref(null)
const seriesListHeight = ref('auto')
const isAtBottom = ref(false)
let resizeObserver = null
let expandTimer = null

const normalizeArticle = (article) => ({
  ...article,
  post_title: article?.post_title || article?.title?.rendered || '',
  permalink: article?.permalink || article?.post_url || article?.link || '',
})

const articles = computed(() => {
  const selectedArticles = props.data?.right_content?.articles

  return Array.isArray(selectedArticles)
    ? selectedArticles.map(normalizeArticle)
    : []
})

const isCollapsed = computed(
  () => collapsed.value && device.value === 'desktop'
)

const articleImage = (article) => {
  const acf = article?.acf || {}

  return (
    acf.carrousel_image ||
    acf.carrouselImage ||
    acf.video_placeholder ||
    acf.videoPlaceholder ||
    {}
  )
}

const articleUrl = (article) =>
  article?.permalink || article?.post_url || article?.link || '#'

const plainText = (value) => String(value || '').replace(/<[^>]*>/g, '')

const updateListHeight = async () => {
  if (isCollapsed.value) return

  await nextTick()

  const list = seriesListRef.value
  const cards = list?.querySelectorAll('.series-card') || []

  if (!list || cards.length <= 4) {
    seriesListHeight.value = 'auto'
    isAtBottom.value = false
    return
  }

  const firstCard = cards[0]
  const fourthCard = cards[3]
  const fourCardHeight =
    fourthCard.offsetTop - firstCard.offsetTop + fourthCard.offsetHeight

  seriesListHeight.value = `${fourCardHeight}px`

  await nextTick()
  updateArrowDirection()
}

const updateArrowDirection = () => {
  const list = seriesListRef.value

  if (!list) {
    isAtBottom.value = false
    return
  }

  isAtBottom.value = list.scrollTop >= list.scrollHeight - list.clientHeight - 2
}

const scrollSeries = () => {
  const list = seriesListRef.value
  const firstCard = list?.querySelector('.series-card')

  if (!list || !firstCard) return

  list.scrollTo({
    top: isAtBottom.value ? 0 : list.scrollTop + firstCard.offsetHeight,
    behavior: 'smooth',
  })
}

const collapsePanel = () => {
  window.clearTimeout(expandTimer)
  contentVisible.value = false
  collapsed.value = true
}

const finishPanelExpansion = async () => {
  await updateListHeight()
  contentVisible.value = true
}

const expandPanel = () => {
  window.clearTimeout(expandTimer)
  contentVisible.value = false
  collapsed.value = false

  expandTimer = window.setTimeout(finishPanelExpansion, 550)
}

const handlePanelTransitionEnd = (event) => {
  if (event.propertyName !== 'width' || isCollapsed.value) return

  window.clearTimeout(expandTimer)
  finishPanelExpansion()
}

watch(
  () => props.data?.right_content?.articles,
  () => {
    if (seriesListRef.value) seriesListRef.value.scrollTop = 0
    updateListHeight()
  },
  { flush: 'post' }
)

watch(device, () => {
  if (device.value !== 'desktop' && collapsed.value) {
    collapsed.value = false
    finishPanelExpansion()
  }
})

onMounted(() => {
  updateListHeight()

  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver(updateListHeight)
    if (seriesListRef.value) resizeObserver.observe(seriesListRef.value)
  }

  window.addEventListener('resize', updateListHeight)
})

onBeforeUnmount(() => {
  window.clearTimeout(expandTimer)
  resizeObserver?.disconnect()
  window.removeEventListener('resize', updateListHeight)
})
</script>

<style lang="scss" scoped>
.article-in-this-series-module-component {
  @apply relative overflow-hidden bg-white pt-10 lg:pt-20;

  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr;
  }

  &.no-media-before {
    @apply pt-8 lg:pt-2;
  }

  .sa-container {
    @media (min-width: 1024px) {
      grid-column: 1;
      grid-row: 1;
    }
  }

  .module-layout {
    @apply flex flex-col items-stretch gap-5 transition-[padding] duration-500 ease-out;

    @media (min-width: 1024px) {
      padding-right: max(0px, calc(1020px - 50vw));
    }

    &.is-series-collapsed {
      @media (min-width: 1024px) {
        padding-right: max(0px, calc(717px - 50vw));
      }
    }

    .left-content {
      @apply min-w-0 w-full lg:py-3 lg:pr-4;

      :deep(h1) {
        @apply header-1 normal-case mb-5 text-balanced;
      }

      :deep(h2) {
        @apply header-2 normal-case mb-5 text-balanced;
      }
      :deep(h3) {
        @apply header-3 normal-case mb-5 text-balanced;
      }

      :deep(h4) {
        @apply header-4 normal-case mb-5 text-balanced;
      }
      :deep(h5) {
        @apply header-5 normal-case mb-5 text-balanced;
      }

      :deep(h6) {
        @apply header-6 normal-case mb-5 text-balanced;
      }

      :deep(p),
      :deep(li) {
        @apply text-[16px] leading-[1.25] text-balanced;
      }

      :deep(p),
      :deep(ul),
      :deep(ol) {
        @apply mb-5;
      }

      :deep(ul) {
        @apply list-disc pl-6;
      }

      :deep(ol) {
        @apply list-decimal pl-6;
      }
    }
  }

  .series-panel {
    @apply relative min-w-0 rounded-tl-[35px] bg-humble transition-[width] duration-500 ease-out lg:z-5 lg:min-h-[300px] lg:rounded-tl-[50px];
    @apply lg:overflow-hidden;

    &:before {
      @apply content-[''] absolute -right-full top-0 h-full w-full bg-humble z-2 lg:hidden;
    }

    @media (min-width: 1024px) {
      grid-column: 1;
      grid-row: 1;
      justify-self: end;
      width: 560px;
    }

    &.is-collapsed {
      @apply lg:w-[257px];

      .series-content {
        @apply lg:min-h-0 lg:overflow-hidden lg:px-0 lg:py-0;
        max-height: 0;
      }
    }

    .collapsed-trigger {
      @apply absolute top-0 left-0 z-10 hidden w-full cursor-pointer items-center gap-1.5 border-0 bg-transparent px-6 py-9 text-left lg:flex;

      svg {
        @apply h-7 w-7 flex-none text-approachable;
      }

      span {
        @apply whitespace-nowrap text-[18px] leading-[1.2] text-white lg:text-[26px];
      }
    }

    .series-content {
      @apply pointer-events-none invisible relative flex min-h-full flex-col px-4 pt-8 pb-16 opacity-0 md:px-7 lg:px-[60px] lg:py-[70px];
      max-height: 2000px;
      transition: opacity 200ms ease-out, max-height 500ms ease-out,
        min-height 500ms ease-out, padding 500ms ease-out;

      &.is-content-visible {
        @apply pointer-events-auto visible opacity-100;
      }

      .series-header {
        @apply flex items-center justify-between gap-5 pb-4;

        h2 {
          @apply m-0 text-[18px] font-normal leading-[1.2] text-white lg:text-[26px];
        }

        .collapse-trigger {
          @apply hidden cursor-pointer border-0 bg-transparent p-0 lg:block;

          svg {
            @apply block text-approachable;

            path {
              @apply text-approachable;
            }
          }
        }
      }

      .series-list-frame {
        @apply relative;

        .series-list {
          @apply overflow-hidden;

          .series-card {
            @apply py-3;

            :deep(.bicolor-line-component) {
              @apply h-[2px];
            }

            .card-content {
              @apply mt-6 grid grid-cols-1 gap-3 md:grid-cols-[44%_minmax(0,1fr)] md:gap-4;

              .card-image {
                @apply relative block w-full overflow-hidden rounded-[8px] bg-[#D9D9D9] no-underline;
                aspect-ratio: 16 / 9;

                > img {
                  @apply h-full w-full object-cover object-center transition-transform duration-300 ease-out;
                }

                .card-player {
                  @apply pointer-events-none absolute left-1/2 top-1/2 z-2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 transition-transform duration-300;
                  background: rgba(255, 255, 255, 0.42);
                  box-shadow: inset 0 2px 1px rgba(255, 255, 255, 0.15);
                  -webkit-backdrop-filter: blur(20px);
                  backdrop-filter: blur(20px);

                  svg {
                    @apply h-4 w-4 translate-x-[1px];
                  }
                }

                &:hover,
                &:focus-visible {
                  > img {
                    @apply scale-105;
                  }

                  .card-player {
                    @apply scale-110;
                  }
                }
              }

              .card-text {
                h3 {
                  @apply m-0 text-[16px] font-normal leading-[1.2] text-white lg:text-[18px];
                }

                .description {
                  @apply mt-2;

                  :deep(*) {
                    @apply m-0 text-[14px] leading-[1.2] text-white/70 line-clamp-2 lg:text-[16px];
                  }
                }
              }
            }
          }
        }

        .scroll-control {
          @apply absolute flex right-0 -bottom-10 h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 bg-progressive p-0 text-humble;
          @apply lg:bottom-3;

          svg {
            @apply text-humble transition-transform duration-300 ease-out;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
body.article-in-this-series-humble-footer .site-footer {
  background: #0e4343 !important;

  @media (min-width: 1024px) {
    background: linear-gradient(
      to right,
      #fff 0%,
      #fff calc(100% - 560px),
      #0e4343 calc(100% - 560px),
      #0e4343 100%
    ) !important;
  }
}
</style>
